import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BranchCode } from "api/risk";
import { CopropertyEligibilityStatus, UpdateCopropertyClaimsHistoryCommand } from "api/coproperty";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { useRefreshSubscriptionProcess } from "../hooks/useRefreshSubscriptionProcess";
import { ClaimsFormState, CopropertyClaimsForm } from "./CopropertyClaimsForm";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertyStepLayout } from "./CopropertyStepLayout";
import { toVerdict } from "./CopropertyFieldControls";
import { useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/**
 * Sinistralité déclarative du dossier MRI (écran 16), dernier écran avant la synthèse
 * d'éligibilité. Le bouton d'étape attend les réponses à l'historique d'assurance et à la
 * renonciation à recours réciproque et, quand des sinistres sont annoncés, une ligne détaillée ;
 * il enregistre puis lit le verdict d'éligibilité global de la réponse.
 */
export const CopropertyClaimsPage = () => {
  const { riskId, copropertyId, coproperty, isLoading, updateClaimsHistory } = useCoproperty();
  const { refresh } = useRefreshSubscriptionProcess();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [claimsState, setClaimsState] = useState<ClaimsFormState>({
    situation: null,
    hasDeclaredClaims: false,
    historyAnswered: false,
  });

  /**
   * Sinistres annoncés sans ligne détaillée, ou historique d'assurance / renonciation sans
   * réponse : l'étape reste ouverte.
   */
  const claimsMissing =
    (claimsState.situation === "declared" && !claimsState.hasDeclaredClaims) || !claimsState.historyAnswered;

  const handleClaimsStateChange = useCallback((state: ClaimsFormState) => setClaimsState(state), []);

  const hasCoproperty = copropertyId != null;

  useEffect(() => {
    if (!riskId || hasCoproperty) return;

    toast.warn("Rattachez d'abord une copropriété.");
    navigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`, { replace: true });
  }, [riskId, hasCoproperty, navigate]);

  const buildCommandRef = useRef<(() => UpdateCopropertyClaimsHistoryCommand | null) | null>(null);
  const registerBuildCommand = useCallback((build: () => UpdateCopropertyClaimsHistoryCommand | null) => {
    buildCommandRef.current = build;
  }, []);

  const summaryRoute = `${MRI_ROUTES.eligibilitySummary}/${riskId}`;

  const saveAndContinue = useCallback(async () => {
    const command = buildCommandRef.current?.();
    if (!command) return;

    setIsSubmitting(true);
    try {
      const response = await updateClaimsHistory(command);
      if (!response) return;

      const result = toVerdict(response.eligibility);
      if (!result) {
        toast.error("La vérification d'éligibilité a échoué — réessayez.");
        return;
      }

      await refresh();

      if (result.status === CopropertyEligibilityStatus.NonEligible) {
        navigate("/excluded-coverage", {
          state: {
            message: "Cette copropriété ne peut pas être assurée en MRI",
            reasons: result.reasons,
            coverageType: "MRI",
            secondaryButtonText: "Revoir la sinistralité",
            secondaryButtonRoute: `${MRI_ROUTES.copropertyClaims}/${riskId}`,
          },
        });
        return;
      }

      if (result.status === CopropertyEligibilityStatus.ACompleter) {
        toast.warn("Sinistralité enregistrée, mais des informations manquent.");
        return;
      }

      navigate(summaryRoute);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  }, [updateClaimsHistory, refresh, navigate, summaryRoute, riskId]);

  const branchStepConfig = useMemo<BranchStepConfig>(
    () => ({
      [BranchCode.Mri]: createStepConfig({
        prev: {
          identifier: MriStepIdentifier.CopropertyComplements,
          route: MRI_ROUTES.copropertyComplements,
          label: "Retour aux compléments",
        },
        next: {
          identifier: MriStepIdentifier.Pricing,
          route: MRI_ROUTES.eligibilitySummary,
          label: isSubmitting ? "Enregistrement…" : "Enregistrer et continuer",
          disabled: isSubmitting || claimsMissing,
          onClick: saveAndContinue,
        },
      }),
    }),
    [isSubmitting, claimsMissing, saveAndContinue]
  );
  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(branchStepConfig);

  if (!hasCoproperty) {
    return <GenericLoading isPageCentered />;
  }

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        {isLoading || !coproperty ? (
          <GenericLoading isPageCentered />
        ) : (
          <CopropertyStepLayout
            title="Sinistralité"
            message="Indiquez la situation de sinistralité des trois derniers exercices. Si des sinistres sont déclarés, ils sont à détailler ici. Le relevé d'information de sinistralité sera demandé à la contractualisation dans tous les cas, avec ou sans sinistre."
            aside={
              <>
                <CopropertyIdentityCard coproperty={coproperty} />
                <CopropertyRecapPanel
                  coproperty={coproperty}
                  hideIdentity
                  showRetainedArea
                  checklist={[
                    { label: "Copropriété rattachée", status: "done" },
                    { label: "Questionnaire éligible", status: "done" },
                    { label: "Bâtiments décrits", status: "done" },
                    { label: "Compléments", status: "done" },
                    { label: "Sinistralité en cours", status: "active" },
                  ]}
                />
              </>
            }
          >
            <CopropertyClaimsForm
              coproperty={coproperty}
              registerBuildCommand={registerBuildCommand}
              onStateChange={handleClaimsStateChange}
            />
          </CopropertyStepLayout>
        )}
      </StepPageWrapper>
    </div>
  );
};
